import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Fade,
  IconButton,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { styled } from "@mui/system";
import React, { KeyboardEvent, useEffect, useRef, useState } from "react";

type ExpandableSearchBoxProps = {
  onSearch?: (query: string) => void;
};

const ExpandableBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  position: "relative",
});

const AnimatedTextField = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(() => ({
  width: 0,
  transition: "width 0.3s ease",
  "&.expanded": {
    width: "250px",
  },
}));

const ExpandableSearchBox = ({ onSearch }: ExpandableSearchBoxProps) => {
  const [expanded, setExpanded] = useState(false);
  const [searchText, setSearchText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce logic
  useEffect(() => {
    if (!expanded) return;
    const handler = setTimeout(() => {
      onSearch?.(searchText.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText, onSearch, expanded]);

  const handleExpand = () => {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleCollapse = () => {
    setSearchText("");
    onSearch?.("");
    setExpanded(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onSearch) {
      onSearch(searchText.trim());
    }
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    // Removed immediate onSearch call for debounce
  };

  return (
    <ExpandableBox>
      <AnimatedTextField
        className={expanded ? "expanded" : ""}
        variant="standard"
        size="small"
        inputRef={inputRef}
        value={searchText}
        onChange={onChangeHandler}
        onKeyDown={handleKeyDown}
        placeholder="Search..."
        InputProps={{
          disableUnderline: false,
        }}
      />

      <Fade in={!expanded} timeout={200} unmountOnExit>
        <IconButton onClick={handleExpand} aria-label="Open search">
          <SearchIcon color="primary" />
        </IconButton>
      </Fade>

      <Fade in={expanded} timeout={200} unmountOnExit>
        <IconButton
          onClick={handleCollapse}
          aria-label="Close search"
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </Fade>
    </ExpandableBox>
  );
};

export default ExpandableSearchBox;
